import { useMemo, useState, useEffect } from "react"
import { Search, Trash2 } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../components/ui/dialog"
import { Badge } from "../components/ui/badge"
import { useCounts } from "../components/CountProvider"
import { useLocale } from "../components/LocaleProvider"
import { api } from "../lib/api"
import { toast } from "../components/ui/toast"
import type { DumpRecord } from "../types"

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B"
  const units = ["B", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function Dumps() {
  const { setCount } = useCounts()
  const { t } = useLocale()
  const [dumps, setDumps] = useState<DumpRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [deleteTarget, setDeleteTarget] = useState<DumpRecord | null>(null)

  const fetchDumps = async () => {
    try {
      const data = await api.getDumps()
      setDumps(data)
    } catch (err: any) {
      toast(err.message, "error")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDumps()
  }, [])

  useEffect(() => {
    setCount("dumps", dumps.length)
  }, [dumps.length, setCount])

  const filtered = useMemo(
    () =>
      dumps.filter((row) =>
        Object.values(row).some((v) => String(v).toLowerCase().includes(search.toLowerCase())),
      ),
    [dumps, search],
  )

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await api.deleteDump(deleteTarget.id)
      toast(t("toast_dump_deleted"))
      setDumps((prev) => prev.filter((d) => d.id !== deleteTarget.id))
      setDeleteTarget(null)
    } catch (err: any) {
      toast(err.message, "error")
    }
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center text-muted-foreground">
        {t("loading")}
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("search_dumps")}
            className="h-10 w-full max-w-sm rounded-md border border-input bg-background pl-10 pr-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">{t("th_time")}</TableHead>
              <TableHead>{t("th_filename")}</TableHead>
              <TableHead className="w-24">{t("th_file_size")}</TableHead>
              <TableHead className="w-24">{t("th_duration")}</TableHead>
              <TableHead className="w-28">{t("th_dump_status")}</TableHead>
              <TableHead className="w-48">{t("th_error")}</TableHead>
              <TableHead className="w-20">{t("th_actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  {t("empty_no_dumps")}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-sm text-muted-foreground whitespace-nowrap">
                    {new Date(row.createdAt).toLocaleString("de-DE", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{row.filename}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    {formatFileSize(row.fileSize)}
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    {formatDuration(row.duration)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={row.status === "SUCCESS" ? "success" : "danger"}>
                      {row.status === "SUCCESS"
                        ? t("dump_status_success")
                        : t("dump_status_failed")}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-48 truncate">
                    {row.error || "—"}
                  </TableCell>
                  <TableCell>
                    <Dialog
                      open={deleteTarget?.id === row.id}
                      onOpenChange={(v) => {
                        if (!v) setDeleteTarget(null)
                      }}
                    >
                      <DialogTrigger asChild>
                        <button
                          onClick={() => setDeleteTarget(row)}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>{t("dialog_delete_dump")}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <p className="text-sm text-muted-foreground">
                            {t("dialog_delete_dump_confirm")}
                          </p>
                          <p className="font-mono text-sm">{row.filename}</p>
                          <div className="flex justify-end gap-2">
                            <DialogClose asChild>
                              <button className="h-9 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors">
                                {t("btn_cancel")}
                              </button>
                            </DialogClose>
                            <button
                              onClick={handleDelete}
                              className="h-9 px-4 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                            >
                              {t("btn_delete")}
                            </button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default Dumps
